/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 */
package ai

import kotlinx.serialization.Serializable

/**
 * Content payload for the Wiz 3R card detail screen (Phase 2b).
 *
 * Pure data — no platform-specific types. Web reads this and renders HTML;
 * iOS and Android (later) read the same shape and render native UI. The
 * filtering / math logic that produces this payload lives in
 * [Wiz3RDetailContentBuilder] and is also platform-independent.
 */
@Serializable
data class Wiz3RDetailContent(
    val reveal: RevealContent,
    val reason: ReasonContent,
    val realize: RealizeContent,
)

/** Source rows that backed the LLM's REVEAL claim — what produced the cited numbers. */
@Serializable
data class RevealContent(
    val heading: String,
    val subheading: String? = null,
    val rows: List<DetailRow> = emptyList(),
    val emptyMessage: String? = null,
    /** Optional comparison block (e.g. "vs last month"). */
    val comparison: RevealComparison? = null,
)

/** Small "this period vs that period" summary above the row list. */
@Serializable
data class RevealComparison(
    val currentLabel: String,
    val currentValue: String,
    val previousLabel: String,
    val previousValue: String,
    val deltaText: String? = null,
)

@Serializable
data class DetailRow(
    val title: String,
    val subtitle: String? = null,
    /** Pre-formatted amount string (already includes currency symbol). */
    val amountText: String,
    /** Optional hex color for the amount (income green vs neutral, etc.). */
    val amountColorHint: String? = null,
)

/** The math + chart that backed the LLM's REASON claim. */
@Serializable
data class ReasonContent(
    /** Reuses the existing ChartSpec; web already has a renderer (Wiz3RChart). */
    val chart: ChartSpec? = null,
    /** Step-by-step math lines (label / expression / result). */
    val mathLines: List<MathLine> = emptyList(),
    /** Free-form explainer text shown below the math. */
    val explainer: String? = null,
)

@Serializable
data class MathLine(
    val label: String,
    /** e.g. "$2,300 / $12,522" — the formula or input pair. */
    val expression: String? = null,
    /** e.g. "18.4%". May stand alone if no formula. */
    val result: String,
)

/** Action surface for REALIZE. */
@Serializable
data class RealizeContent(
    val intro: String? = null,
    val actions: List<RealizeAction> = emptyList(),
)

@Serializable
data class RealizeAction(
    val label: String,
    /** Matches the existing PersonalizedAdviceCard.actionType strings:
     *  NAVIGATE_GOAL, NAVIGATE_CATEGORY, NAVIGATE_TRANSACTIONS, CREATE_GOAL,
     *  REVIEW_SUBSCRIPTIONS, NAVIGATE_GUIDE, NONE.
     */
    val actionType: String,
    val actionData: Map<String, String> = emptyMap(),
    /** Visual emphasis — true for the primary CTA, false for secondary. */
    val primary: Boolean = false,
)
